import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { FacilityParams } from 'app/models/facility-params'
import { Hold } from 'app/models/hold-params'
import HoldIndicator, { holdsToEntries, isLiftable } from './hold-indicator'
import LiftHoldModal from './holds/lift-hold-modal'
import { liftFacilityHoldAction } from 'app/views/pages/deals-management/facilities/facility-details/facility-hold-actions'

// Facility-hold indicator: the shared origin-aware hold card wired for a
// facility's flat `holds` list. Hover shows the read-only card; with
// enableLift, a direct LendOS facility hold gets an in-card Lift button (the
// Facilities table + facility header + deal's related-facilities table). A
// hold cascaded down from the deal, or owned by another system (SigningPortal),
// is read-only here — it's lifted at its origin instead.
export default function FacilityHoldIndicator({
  facility,
  enableLift,
}: {
  facility?: FacilityParams
  enableLift?: boolean
}) {
  const dispatch = useDispatch()
  const [liftHold, setLiftHold] = useState<Hold | null>(null)

  if (!facility) return null

  const makeFacilityLift = (h: Hold) =>
    enableLift && isLiftable(h, 'Facility') ? () => setLiftHold(h) : undefined

  const entries = holdsToEntries(facility.holds ?? [], 'Facility', makeFacilityLift)

  if (entries.length === 0) return null

  return (
    // Stops clicks inside the indicator OR its (React-child) lift modal from
    // bubbling to a clickable table row — React portals bubble events through
    // the React tree, so even the portaled modal's Cancel/Submit would
    // otherwise trigger the row's navigation.
    <span style={{ display: 'inline-flex' }} onClick={e => e.stopPropagation()}>
      <HoldIndicator
        holds={entries}
        variant="auto"
        interaction="click"
        // Accordion (one panel open, the rest collapsed headers) only when there
        // is more than one lock/hold to show — matching the Trades card; a single
        // one renders plainly.
        layout={entries.length > 1 ? 'accordion' : 'stacked'}
        ariaLabel="Facility hold or lock status"
        size={24}
      />
      <LiftHoldModal
        opened={!!liftHold}
        close={() => setLiftHold(null)}
        entityNoun="facility"
        hold={liftHold}
        onSubmit={(liftReason?: string) =>
          liftFacilityHoldAction(facility.id, liftReason, dispatch)
        }
      />
    </span>
  )
}
